import { motion } from "framer-motion";
import {
  Play,
  MapPin,
  MousePointer2,
  Keyboard,
  BookOpen,
  ChevronRight,
  Layers,
} from "lucide-react";
import { useLiveTour } from "../../context/LiveTourContext";

export default function LiveTourPanel() {
  const { steps, pageTours, startTour, startPageTour, active, tourLabel } =
    useLiveTour();

  if (active) {
    return (
      <div className="rounded-2xl p-6 bg-indigo-50 border border-indigo-200 text-center">
        <p className="text-sm font-medium text-indigo-800">
          <strong>{tourLabel}</strong> is running — follow the spotlight on each
          button, card, and panel. Press Esc to exit.
        </p>
      </div>
    );
  }

  const totalSteps = steps.length;

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0a1628 0%, #0f2d6b 50%, #112366 100%)",
          boxShadow: "0 12px 40px rgba(15,23,42,0.2)",
        }}
      >
        <div className="p-8 sm:p-10 relative">
          <div
            className="absolute inset-0 opacity-50 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 20% 30%, rgba(99,102,241,0.45) 0%, transparent 50%)",
            }}
          />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(260px,360px)] xl:grid-cols-[minmax(0,1fr)_minmax(280px,400px)] gap-8 lg:items-center">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-sky-200 text-xs font-medium mb-4">
                <MousePointer2 size={14} />
                Live on real pages
              </div>
              <h2 className="text-2xl font-display font-bold text-white mb-3">
                Interactive Live Tours
              </h2>
              <p className="text-sky-100/90 text-sm sm:text-base leading-relaxed mb-4">
                Each module has its own tour with step-by-step explanations for
                every button, stat card, and panel — like Userpilot or WalkMe.
                Start the full journey or pick a single page below. Only modules
                included in your organisation&apos;s license are shown.
              </p>
              <ul className="space-y-2 text-xs text-sky-200/90 mb-6">
                <li className="flex items-center gap-2">
                  <MapPin size={12} /> {pageTours.length} module pages ·{" "}
                  {totalSteps} total steps
                </li>
                <li className="flex items-center gap-2">
                  <Keyboard size={12} /> Arrow keys to move · Esc to close
                </li>
                <li className="flex items-center gap-2">
                  <BookOpen size={12} /> On Gap, ROPA, PIA, and TPRM, the tour
                  temporarily loads your first department so every spotlight
                  can attach to the real form
                </li>
              </ul>
              <button
                type="button"
                onClick={() => startTour(0)}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-white text-indigo-700 font-semibold text-sm hover:bg-sky-50 transition-colors shadow-xl"
              >
                <Play size={18} fill="currentColor" />
                Start Full Tour
              </button>
            </div>
            <div className="space-y-2 w-full">
              <p className="text-[10px] font-bold uppercase tracking-wide text-sky-300/80 mb-2">
                What you&apos;ll learn
              </p>
              {[
                "What each nav item opens",
                "What every button does",
                "How stat cards and tables work",
                "How to submit assessments and reports",
              ].map((item) => (
                <div
                  key={item}
                  className="px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-xs text-sky-100"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Layers size={18} className="text-indigo-600" />
          <h3 className="text-lg font-bold text-slate-900 font-display">
            Tour by module page
          </h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          New to a specific area? Start only that page&apos;s tour — each opens
          the real screen and explains every important control.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {pageTours.map((page) => (
            <motion.button
              key={page.id}
              type="button"
              onClick={() => startPageTour(page.id)}
              whileHover={{ y: -2 }}
              className="text-left rounded-2xl p-4 border border-slate-200/80 bg-white/80 hover:border-indigo-200 hover:shadow-md transition-all group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                  <BookOpen size={16} className="text-indigo-600" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                  {page.stepCount} steps
                </span>
              </div>
              <h4 className="text-sm font-bold text-slate-900 mb-1">{page.title}</h4>
              <p className="text-xs text-slate-500 line-clamp-2 mb-3">
                {page.description}
              </p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 group-hover:gap-2 transition-all">
                Tour this page
                <ChevronRight size={14} />
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}
